import {
  TrendingUp,
  Scale,
  Layers,
  Waves,
  BarChart3,
  ClipboardList,
  Radio,
  Trophy,
  Calculator,
  GraduationCap,
  BookOpen,
  Shield,
  Zap,
  MessagesSquare,
  Users,
  LucideIcon,
  Table2,
} from "lucide-react";

/**
 * A single navigation destination.
 *  - `label`  full text shown in the desktop rail + mobile drawer.
 *  - `short`  compact text shown under the icon in the mobile bottom bar.
 *  - `tab`    when true the item is eligible for the mobile bottom bar.
 *  - `admin`  gate to ADMIN-role users only.
 */
export interface NavItem {
  to: string;
  label: string;
  short: string;
  icon: LucideIcon;
  tab?: boolean;
  admin?: boolean;
  /** end=true → match the path exactly (no descendant matching). */
  end?: boolean;
}

export interface NavSection {
  /** Optional eyebrow shown above the group when the rail is expanded. */
  title?: string;
  items: NavItem[];
}

/**
 * The app's product navigation, grouped the way OddsJam groups its
 * sidebar: the money-making tools first, then trackers, then the
 * education surfaces. Order here drives the desktop rail, the mobile
 * drawer, and (for the three paths in BOTTOM_NAV_PATHS) the mobile
 * bottom bar.
 */
export const NAV_SECTIONS: NavSection[] = [
  {
    title: "Tools",
    items: [
      { to: "/positive-ev", label: "Positive EV", short: "+EV", icon: TrendingUp, tab: true },
      { to: "/arbitrage", label: "Arbitrage", short: "Arbs", icon: Scale, tab: true },
      { to: "/odds", label: "Odds Screen", short: "Odds", icon: Table2, tab: true },
      { to: "/parlay-builder", label: "Parlay Builder", short: "Parlays", icon: Layers, tab: true },
      { to: "/prediction-traders", label: "Traders", short: "Traders", icon: Waves, tab: true },
      {
        to: "/sharp-money",
        label: "Sharp Money",
        short: "Sharp",
        icon: Zap,
        tab: true,
      },
    ],
  },
  {
    title: "Community",
    items: [
      { to: "/feed", label: "Feed", short: "Feed", icon: MessagesSquare, tab: true },
      { to: "/communities", label: "Communities", short: "Groups", icon: Users },
    ],
  },
  {
    title: "Track",
    items: [
      { to: "/my-bets", label: "My Bets", short: "My Bets", icon: ClipboardList, tab: true },
      { to: "/performance", label: "Brawler Record", short: "Record", icon: BarChart3, tab: true },
      { to: "/live", label: "Live Show", short: "Live", icon: Radio, tab: true },
      { to: "/13-run-pool", label: "13 Run Pool", short: "Run Pool", icon: Trophy },
    ],
  },
  {
    title: "Learn",
    items: [
      { to: "/betting-calculators", label: "Calculators", short: "Calcs", icon: Calculator },
      { to: "/betting-education", label: "Betting Education", short: "Learn", icon: GraduationCap },
      { to: "/blog", label: "Blog", short: "Blog", icon: BookOpen },
    ],
  },
  {
    items: [
      { to: "/admin", label: "Admin", short: "Admin", icon: Shield, admin: true, end: false },
    ],
  },
];

/**
 * The three product destinations that earn a permanent slot in the mobile
 * bottom bar. Five slots total: these, then notifications and help (or,
 * signed out, plans and sign-in) — see BottomNav.
 *
 * They're resolved out of NAV_SECTIONS rather than redeclared so the bar
 * can't drift from the rail it mirrors: same icon, same short label.
 */
const BOTTOM_NAV_PATHS = ["/positive-ev", "/arbitrage", "/feed"];

export const BOTTOM_NAV_ITEMS: NavItem[] = BOTTOM_NAV_PATHS.map((path) =>
  NAV_SECTIONS.flatMap((section) => section.items).find(
    (item) => item.to === path,
  ),
).filter((item): item is NavItem => item != null);

/**
 * The bottom bar is product navigation, so it stands down on the marketing
 * landing page and inside the self-contained admin console (which ships its
 * own nav). BottomNav flags the routes where it renders on <html>, and the
 * content wrapper reserves space from that flag, so the gap under the page
 * always matches what's actually on screen.
 */
export const shouldShowBottomNav = (pathname: string): boolean =>
  pathname !== "/" && !pathname.startsWith("/admin");

/**
 * Funnel routes with no app chrome at all: no nav, no footer, no popups. The
 * page is the whole offer, so nothing on it should lead anywhere else.
 */
export const isChromelessRoute = (pathname: string): boolean =>
  /^\/start(\/|$)/.test(pathname);

/**
 * Detail routes that own the whole phone/tablet screen. They ship their own
 * top header (a back button and the record's title), so the app top bar steps
 * aside — the X-mobile pattern where opening a post replaces the app chrome
 * rather than stacking another bar on top of it. The bottom bar stays: it is
 * the way back out.
 *
 * Desktop is unaffected: the rail has room to spare there.
 */
const IMMERSIVE_ROUTES: RegExp[] = [
  /^\/feed\/post\/\d+\/?$/,
  /^\/communities\/new\/?$/,
  /^\/communities\/\d+\/?$/,
  /^\/communities\/\d+\/(edit|members)\/?$/,
];

export const isImmersiveRoute = (pathname: string): boolean =>
  IMMERSIVE_ROUTES.some((pattern) => pattern.test(pathname));
